/**
 * What a delivery failure means to the person who owns the destination.
 *
 * The worker records a closed vocabulary of `error_class` values for metrics and
 * for the failure log, and the API returns the stored string unchanged. Those are
 * storage labels: `http_4xx` tells an operator which counter moved and tells a
 * customer nothing about whether to wait, replace a credential, or call an admin.
 *
 * Retries here span seconds, so a failed notification is not coming later, and
 * for a destination the customer configured the customer is the only person who can
 * fix it. That makes this table the recovery path, not decoration.
 *
 * Mirrors `shared_clickhouse::ERROR_CLASSES`. A class with no entry falls back to
 * generic copy rather than printing the raw label.
 */

/**
 * @typedef {object} FailureCopy
 * @property {string} summary What went wrong, in the customer's terms.
 * @property {string} action What to do about it.
 */

/** @type {Record<string, FailureCopy>} */
const FAILURES = {
    circuit_open: {
        summary: "Sending is paused after repeated failures",
        action: "Fix the problem below, then sending resumes on its own.",
    },
    connect_failed: {
        summary: "We could not reach the destination",
        action: "Check the destination is still online, then try again.",
    },
    credential_invalid: {
        summary: "The stored credential was rejected",
        action: "Remove this destination and add it again with fresh credentials.",
    },
    decrypt_failed: {
        summary: "We could not read the stored credential",
        action: "Remove this destination and add it again.",
    },
    host_not_allowed: {
        summary: "The address is not one we can send to",
        action: "Remove this destination and add it again with a supported destination.",
    },
    http_4xx: {
        summary: "The destination rejected the message",
        action: "The credential may have been revoked or deleted. Create a new one and add it again.",
    },
    http_5xx: {
        summary: "The destination reported an error",
        action: "Usually temporary. If it keeps happening, check the destination's status.",
    },
    malformed_url: {
        summary: "The stored address is not a valid URL",
        action: "Remove this destination and add it again.",
    },
    missing_destination: {
        summary: "The destination has no address stored",
        action: "Remove this destination and add it again.",
    },
    missing_host: {
        summary: "The stored address has no host",
        action: "Remove this destination and add it again.",
    },
    path_not_allowed: {
        summary: "The address is not an endpoint we can send to",
        action: "Check the details you entered, then add it again.",
    },
    rate_limited: {
        summary: "The destination is asking us to slow down",
        action: "Increase this destination's batching window so it sends less often.",
    },
    redirect_refused: {
        summary: "The destination redirected us somewhere we will not follow",
        action: "Use the destination's direct address rather than a redirect or short link.",
    },
    render_failed: {
        summary: "We could not build the message",
        action: "Contact support -- this one is on us, not on your configuration.",
    },
    timeout: {
        summary: "The destination did not answer in time",
        action: "Usually temporary. If it keeps happening, check the destination's status.",
    },
    transport_error: {
        summary: "The connection to the destination failed",
        action: "Usually temporary. If it keeps happening, check the destination is reachable.",
    },
    unsupported_scheme: {
        summary: "The stored address is not https",
        action: "Remove this destination and add it again with an https URL.",
    },
};

/** @type {FailureCopy} */
const UNKNOWN = {
    summary: "The last notification could not be delivered",
    action: "If it keeps happening, contact support.",
};

/**
 * Customer-facing copy for a stored `error_class`.
 *
 * @param {string} errorClass
 * @returns {FailureCopy}
 */
export function failureCopy(errorClass) {
    return FAILURES[errorClass] ?? UNKNOWN;
}

/** Every class this table has copy for. Exported for the parity test. */
export const KNOWN_ERROR_CLASSES = Object.keys(FAILURES).sort();
